import { type Message, randomUUID } from '@ag-ui/client';
import is from '@sindresorhus/is';
import noop from 'lodash/noop';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import {
    ChatAgentRegistry,
    type ChatAgentSnapshot,
    type ChatHistoryStatus,
    type ChatLiveTurn,
    type ChatNotice,
    type ChatRunSettlement,
    type ChatUserTurn,
    CobrainerChatAgent,
    findAnswerableQuestionId,
    liveTurnOf,
    useChatAgent,
} from '~/shared/agent-chat';
import { useApplicationContext } from '~/shared/context/use-application-context';
import { createTimeOrderedUuid } from '~/shared/uuid/uuid';

import {
    type AgUiUserMessageCommand,
    type ChatAttachmentRef,
    type FrontendContext,
    type Thread,
    toAgUiUserContent,
} from '../../../data-contract/chat';
import { reportDroppedChatStreamEvent } from '../../../data-dog';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { useCancelChatTurn, useChatThreads, useCreateChatThread, useMarkChatThreadRead } from '../../api';
import { cancelTurnWithRetry } from '../../api/cancel-turn-with-retry';
import type {
    AnswerQuestionOptions,
    ChatConversation,
    EnsureThreadIdOptions,
    SendCreateJsaEntitiesOptions,
    SendOptions,
    SendRegenerateOptions,
} from '../../chat-conversation';
import { useChatOpen, useChatStore } from '../../stores/chat-open-store';
import { getAgUiThreadStatus, type ThreadStatus } from '../../thread-status';
import { useChatAgentQueryCache } from '../api/use-chat-agent-query-cache';
import { readUserMetadata } from '../chat-transcript';
import { useChatAutoClose } from './use-chat-auto-close';

type PreparedSendTextOptions = {
    readonly attachments?: readonly ChatAttachmentRef[];
    readonly frontendContext?: FrontendContext;
    readonly isComposerMessage?: boolean;
    readonly message: string;
    readonly messageType: 'text';
    readonly threadId?: string;
};

type PreparedSendOptions = PreparedSendTextOptions | SendRegenerateOptions | SendCreateJsaEntitiesOptions;

/** The AG-UI conversation: the thread as its messages, and the own run on the turn it belongs to. */
export type AgUiChatConversation = ChatConversation & {
    /** The active thread's history: `loading` only until it loaded once, `failed` when the last load failed. */
    readonly history: ChatHistoryStatus;
    /** What the own run shows on the turn it belongs to. */
    readonly liveTurn: ChatLiveTurn | null;
    /** The active thread's messages, oldest first. */
    readonly messages: readonly Message[];
    /**
     * Why the latest request did not start: `busy` while another run holds the thread (including a
     * second send while one is still queued), `send-failed` for any other rejection. The input is kept.
     */
    readonly notice: ChatNotice | null;
    /** The activity asking the question the user can answer now. */
    readonly openQuestionId: string | null;
    /** Runs the failed latest turn opened by the user message `requestMessageId` again. */
    readonly retry: (requestMessageId: string) => void;
};

const toUserTurn = (
    command: AgUiUserMessageCommand,
    text: string,
    frontendContext: FrontendContext | undefined,
    attachments: readonly ChatAttachmentRef[] = [],
): ChatUserTurn => {
    return {
        command,
        ...(is.undefined(frontendContext) ? {} : { frontendContext }),
        message: {
            content: toAgUiUserContent(text, attachments),
            id: createTimeOrderedUuid(),
            metadata: { cobrainer: { command, createdAt: new Date().toISOString() } },
            role: 'user',
        },
    };
};

/** The run command and the user message it opens the turn with; the server keeps the message id. */
const toPreparedUserTurn = (options: PreparedSendOptions): ChatUserTurn => {
    switch (options.messageType) {
        case 'regenerate':
            return toUserTurn(
                { content: options.content, type: 'regenerate' },
                options.content.prompt,
                options.frontendContext,
            );
        case 'create-jsa-entities':
            return toUserTurn(
                { content: options.content, type: 'create-jsa-entities' },
                options.content.prompt,
                options.frontendContext,
            );
        case 'text':
            return toUserTurn({ type: 'new' }, options.message, options.frontendContext, options.attachments);
    }
};

// Agent actions are one-shot requests, not conversation turns: each opens its own thread
// instead of landing in whichever conversation happens to be selected.
const needsFreshThread = (options: PreparedSendOptions): boolean =>
    options.messageType === 'regenerate' || options.messageType === 'create-jsa-entities';

const prepareSendOptions = (options: SendOptions): PreparedSendOptions | null => {
    if (options.messageType === 'regenerate' || options.messageType === 'create-jsa-entities') {
        return options;
    }

    const message = options.message.trim();
    return message.length === 0 ? null : { ...options, message, messageType: 'text' };
};

// A thread is an agent action thread when its first request was a canvas regenerate or an add. A
// history that is not loaded leaves the first request unknown, so such a thread is not treated as one.
const isAgentActionThread = (snapshot: ChatAgentSnapshot): boolean => {
    if (snapshot.history !== 'loaded') return false;
    const firstRequest = snapshot.messages.find((message) => message.role === 'user');
    if (firstRequest?.role !== 'user') return false;
    const command = readUserMetadata(firstRequest)?.command?.type;
    return command === 'regenerate' || command === 'create-jsa-entities';
};

const deriveThreadStatus = (snapshot: ChatAgentSnapshot, hasOpenQuestion: boolean): ThreadStatus => {
    if (hasOpenQuestion) return 'needs-input';
    if (snapshot.run.phase === 'running' || snapshot.threadStatus === 'running') return 'running';
    return snapshot.threadStatus === 'resumable' ? 'on-hold' : 'idle';
};

export const useChatConversation = (): AgUiChatConversation => {
    const queryCache = useChatAgentQueryCache();
    const { logger } = useApplicationContext();
    const { chatAgentEndpoint } = useApplicationFetchers();
    const cancelChatTurn = useCancelChatTurn();
    const createThread = useCreateChatThread();
    const { mutate: markThreadRead } = useMarkChatThreadRead();
    const isChatOpen = useChatOpen();
    const isChatOpenRef = useRef(isChatOpen);
    isChatOpenRef.current = isChatOpen;
    const clearBlockAnswers = useChatStore((state) => state.clearBlockAnswers);

    const [threadId, setThreadId] = useState<string | null>(null);
    const createSeqRef = useRef(0);
    // The server only cancels a stopped turn once its run lock is free; a new turn taking the lock
    // first would leave the stopped one uncancelled, so sends to the thread wait until the cancel landed.
    const cancellingThreadIdRef = useRef<string | null>(null);
    const activeThreadIdRef = useRef<string | null>(threadId);
    activeThreadIdRef.current = threadId;

    const {
        accept: acceptAutoClose,
        cancel: cancelAutoClose,
        schedule: scheduleAutoClose,
        scheduleId: autoCloseScheduleId,
    } = useChatAutoClose();

    const { invalidateThreadList, subscribeToWindowFocus } = queryCache;

    const runStartedRef = useRef<() => void>(noop);
    runStartedRef.current = (): void => {
        cancelAutoClose();
        invalidateThreadList();
    };
    const runSettledRef = useRef<(settlement: ChatRunSettlement) => void>(noop);
    runSettledRef.current = (settlement): void => {
        invalidateThreadList();
        if (!settlement.resolvesAgentAction || activeThreadIdRef.current !== settlement.threadId) return;
        if (useChatStore.getState().isOpen) scheduleAutoClose();
    };

    // Owns the delayed refetches a context refresh schedules, so they end with the provider.
    const [contextRefreshEffects] = useState(() => queryCache.createContextRefreshEffects());
    const [registry] = useState(
        () =>
            new ChatAgentRegistry({
                createAgent: (agentThreadId) =>
                    new CobrainerChatAgent({
                        fetch: chatAgentEndpoint.fetch,
                        getRequestHeaders: chatAgentEndpoint.getRequestHeaders,
                        threadId: agentThreadId,
                        url: chatAgentEndpoint.url,
                    }),
                // The provider outlives the dock, so a thread is only on screen while the dock is open.
                isThreadVisible: (candidate) => candidate === activeThreadIdRef.current && isChatOpenRef.current,
                onContextRefresh: (envelope) => contextRefreshEffects.apply(envelope),
                onDroppedEvent: reportDroppedChatStreamEvent,
                onRunSettled: (settlement) => runSettledRef.current(settlement),
                onRunStarted: () => runStartedRef.current(),
            }),
    );

    useEffect(
        () => () => {
            registry.dispose();
            contextRefreshEffects.dispose();
        },
        [contextRefreshEffects, registry],
    );

    // A returning window reads the thread again, like React Query's refetch on focus: messages the
    // server adds outside a run (a bulk change's report) only reach the client through a sync run.
    useEffect(() => {
        if (is.null(threadId) || !isChatOpen) return;
        registry.activate(threadId);
        return subscribeToWindowFocus((isFocused) => {
            if (isFocused) registry.activate(threadId);
        });
    }, [isChatOpen, registry, subscribeToWindowFocus, threadId]);

    const previousThreadIdRef = useRef(threadId);
    useEffect(() => {
        if (previousThreadIdRef.current === threadId) return;
        previousThreadIdRef.current = threadId;
        cancelAutoClose();
    }, [cancelAutoClose, threadId]);

    const snapshot = useChatAgent(registry, threadId);
    const runningThreadIds = useSyncExternalStore(registry.subscribeAll, registry.getRunningThreadIds);

    const messages = useMemo(
        () => (is.null(snapshot.failedSend) ? snapshot.messages : [...snapshot.messages, snapshot.failedSend]),
        [snapshot.failedSend, snapshot.messages],
    );
    const liveTurn = useMemo(() => liveTurnOf(snapshot.run), [snapshot.run]);
    const openQuestionId = findAnswerableQuestionId(snapshot);

    // The agent's status is only kept current while the thread is on screen; once the dock closes the
    // thread list's own status takes over, so its polling can stop when the server reports idle.
    const isAgentStatusLive = isChatOpen && snapshot.history === 'loaded';
    const resolveThreadStatus = useCallback(
        (candidate: Thread): ThreadStatus =>
            candidate.id === threadId && isAgentStatusLive
                ? deriveThreadStatus(snapshot, !is.null(openQuestionId))
                : getAgUiThreadStatus(candidate, { hasLiveStream: runningThreadIds.has(candidate.id) }),
        [isAgentStatusLive, openQuestionId, runningThreadIds, snapshot, threadId],
    );
    const threadsQuery = useChatThreads({ enabled: !is.null(threadId), getStatus: resolveThreadStatus });
    const thread = threadsQuery.data?.threads.find((candidate) => candidate.id === threadId);
    const threadName = thread?.name ?? null;
    const threadStatus: ThreadStatus = is.null(threadId)
        ? 'idle'
        : deriveThreadStatus(snapshot, !is.null(openQuestionId));

    const isStreaming = snapshot.run.phase === 'running';

    const selectFreshThread = useCallback(
        (freshThreadId: string): void => {
            registry.markFresh(freshThreadId);
            setThreadId(freshThreadId);
        },
        [registry],
    );

    // An agent action is a one-shot request in a thread of its own, so a new question typed into the
    // composer afterwards opens a new thread. An answer to a question the thread is waiting on stays.
    const leavesAgentActionThread =
        !is.null(threadId) &&
        is.null(openQuestionId) &&
        snapshot.openInterrupts.length === 0 &&
        isAgentActionThread(snapshot);

    const ensureThreadId = useCallback(
        async (options?: EnsureThreadIdOptions): Promise<string | null> => {
            const isLeaving = options?.isComposerMessage === true && leavesAgentActionThread;
            if (!is.null(threadId) && !isLeaving) return threadId;
            // Overlapping creations resolve to the latest thread instead of racing.
            const seq = ++createSeqRef.current;
            const created = await createThread.mutateAsync();
            if (createSeqRef.current !== seq) return null;
            selectFreshThread(created.id);
            return created.id;
        },
        [createThread, leavesAgentActionThread, selectFreshThread, threadId],
    );

    const send = useCallback(
        async (options: SendOptions): Promise<boolean> => {
            const prepared = prepareSendOptions(options);
            if (is.null(prepared)) return false;

            let targetThreadId: string | null;
            if (needsFreshThread(prepared)) {
                createSeqRef.current += 1;
                targetThreadId = randomUUID();
                cancelAutoClose();
                selectFreshThread(targetThreadId);
            } else {
                // A send carrying attachments pins the thread they were presigned against;
                // resolving the thread again here could race a mid-upload thread switch.
                const pinnedThreadId = prepared.messageType === 'text' ? prepared.threadId : undefined;
                const isComposerMessage = prepared.messageType === 'text' && prepared.isComposerMessage === true;
                try {
                    targetThreadId = pinnedThreadId ?? (await ensureThreadId({ isComposerMessage }));
                } catch (error) {
                    logger.error('Chat thread creation failed; the request was not sent', error);
                    return false;
                }
            }
            if (is.null(targetThreadId) || cancellingThreadIdRef.current === targetThreadId) return false;

            return registry.send(targetThreadId, () => toPreparedUserTurn(prepared));
        },
        [cancelAutoClose, ensureThreadId, logger, registry, selectFreshThread],
    );

    const answerQuestion = useCallback(
        ({ actions, blockId }: AnswerQuestionOptions): void => {
            if (is.null(threadId)) return;
            // Once the answer's run settled the thread shows what the server holds: the answered
            // question, or the same question answerable again when the answer did not go through.
            void registry.answer(threadId, blockId, { actions }).finally(() => clearBlockAnswers(blockId));
        },
        [clearBlockAnswers, registry, threadId],
    );

    // A turn whose cancel failed only stays stoppable while its run is still active on the server;
    // once nothing runs, sending a new message is how the thread continues.
    const canStop = isStreaming || snapshot.threadStatus === 'running';

    const stop = useCallback((): void => {
        if (is.null(threadId) || cancellingThreadIdRef.current === threadId) return;
        const stoppedOwnRun = registry.stop(threadId);
        // A run streaming elsewhere has no own run here but is still cancelled.
        if (!stoppedOwnRun && !canStop) return;
        cancellingThreadIdRef.current = threadId;
        void cancelTurnWithRetry(cancelChatTurn, threadId).then((result) => {
            if (cancellingThreadIdRef.current === threadId) cancellingThreadIdRef.current = null;
            if (result.isErr) logger.error('Chat turn cancel failed; the turn stays resumable', result.error);
            invalidateThreadList();
            // The thread is read again so its status reflects the cancel.
            void registry.sync(threadId);
        });
    }, [canStop, cancelChatTurn, invalidateThreadList, logger, registry, threadId]);

    const retry = useCallback(
        (requestMessageId: string): void => {
            if (is.null(threadId) || cancellingThreadIdRef.current === threadId) return;
            cancelAutoClose();
            void registry.retry(threadId, requestMessageId);
        },
        [cancelAutoClose, registry, threadId],
    );

    const selectThread = useCallback(
        (id: string): void => {
            cancelAutoClose();
            createSeqRef.current += 1;
            setThreadId(id);
        },
        [cancelAutoClose],
    );

    const startNewThread = useCallback((): void => {
        cancelAutoClose();
        createSeqRef.current += 1;
        setThreadId(null);
    }, [cancelAutoClose]);

    const isThreadUnread = thread?.unreadMessagesAvailable === true;
    // The provider stays mounted while the dock is closed, so gate on isChatOpen: content the
    // user cannot see must not be marked as read.
    useEffect(() => {
        if (!isChatOpen || is.null(threadId) || !isThreadUnread) return;
        markThreadRead({ threadId });
    }, [isChatOpen, threadId, isThreadUnread, markThreadRead]);

    return useMemo(
        () => ({
            acceptAutoClose,
            answerQuestion,
            autoCloseScheduleId,
            canStop,
            ensureThreadId,
            history: is.null(threadId) ? 'loaded' : snapshot.history,
            isAutoClosePending: !is.null(autoCloseScheduleId),
            // A text send without a thread creates one before its run starts.
            isCreatingThread: createThread.isPending,
            isStreaming,
            isThreadPending: false,
            keepChatOpen: cancelAutoClose,
            liveTurn,
            messages,
            notice: snapshot.notice,
            openQuestionId,
            resolveThreadStatus,
            retry,
            selectThread,
            send,
            startNewThread,
            stop,
            threadId,
            threadName,
            threadStatus,
        }),
        [
            acceptAutoClose,
            answerQuestion,
            autoCloseScheduleId,
            canStop,
            cancelAutoClose,
            createThread.isPending,
            ensureThreadId,
            isStreaming,
            liveTurn,
            messages,
            openQuestionId,
            resolveThreadStatus,
            retry,
            selectThread,
            send,
            snapshot.history,
            snapshot.notice,
            startNewThread,
            stop,
            threadId,
            threadName,
            threadStatus,
        ],
    );
};
